/* eslint-disable react/jsx-no-bind */
import { Component } from 'preact';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import Numeric from './numeric/';
import Graphic from './graphic/';
import Notes from './notes/';
import * as Photometrics from '../photometrics';

import './style';
import 'react-tabs/style/react-tabs.css';

const { atan, PI, pow, round, sqrt, tan } = Math;
const radians = PI / 180;
const radiansDiv2 = radians / 2;

const initialState = {
	beams: [
		// Keep beams flat for shallow copying
		{
			beamAngle: 19,
			lamp: 575,
			distance: 20, // always calculate this
			xFigure: 0,
			yFeet: 0,
			yFace: 5,
			xSource: 20,
			ySource: 25,
			on: true
		},
		{
			beamAngle: 19,
			lamp: 575,
			distance: 20,
			xFigure: 10,
			yFeet: 0,
			yFace: 5,
			xSource: 30,
			ySource: 25,
			on: true
		},
		{
			beamAngle: 19,
			lamp: 575,
			distance: 20,
			xFigure: 20,
			yFeet: 0,
			yFace: 5,
			xSource: 40,
			ySource: 25,
			on: true
		}
	],
	beamIndex: 0,
	settings: {},
	storeVersion: 7
};

const LOCAL_STORAGE_KEY = 'wss-beamcalc';

// Convert float to comma-separated string with no more than 3 significant digits.
function illuminationAsString(illumination) {
	const fullString = round(illumination).toString();
	const fourDigitString = fullString.slice(0, 4);
	const roundedToThreeDigits = fourDigitString.length === 4
		? round(parseInt(fourDigitString, 10) / 10) * 10
		: parseInt(fourDigitString, 10);
	const finalString = `${roundedToThreeDigits}000000000`.slice(0, fullString.length);
	return parseInt(finalString, 10).toLocaleString();
}

export default class App extends Component {

	updateLocalStorage() {
		localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.state));
	}

	updateBeam(beamIndex, newBeam) {
		let newBeams = this.state.beams.slice();
		newBeams.splice(beamIndex, 1, newBeam);
		this.setState({ beams: newBeams });
	}

	recalculate(beamIndex, beam) {
		if (beam) {
			const { beamAngle, xFigure, xSource, yFace, ySource } = beam;
			const xDist = xSource - xFigure;
			const yDist = ySource - yFace;
			beam.distance = xDist;
			beam.beamThrow = round(sqrt(pow(yDist, 2) + pow(beam.distance, 2)));
			beam.elevationAngle = round(atan(yDist / beam.distance) / radians);
			beam.poolWidth = round(beam.beamThrow * tan(beamAngle * radiansDiv2) * 2);
			// fc
			let illumination = Photometrics.illuminationForLensLampThrow(beam.beamAngle, beam.lamp, beam.beamThrow);
			const fc = illuminationAsString(illumination);
			// lux
			illumination = illumination * 10.7639;
			// const lux = illuminationAsString(illumination);
			// beam.illumination = `${fc} fc | ${lux} lux`;
			beam.illumination = `${fc} fc`;
			this.updateBeam(beamIndex, beam);
		} else {
			this.setState({ beamIndex });
		}
	}

	setBeamProperty(beamIndex, property) {
		// Make a copy of the beam
		const beam = Object.assign({}, this.state.beams[beamIndex]);
		// Set the user-changed property
		// @ts-ignore
		const [[key, value]] = Object.entries(property);
		const roundedProperty = { [key]: round(value) };
		Object.assign(beam, roundedProperty);
		this.recalculate(beamIndex, beam);
	}

	setBeamDistance(beamIndex, distance) {
		const beam = { ...this.state.beams[beamIndex] };
		beam.xSource = beam.xFigure + round(distance);
		this.recalculate(beamIndex, beam);
	}

	// Action dispatcher for updating state
	dispatch(action) {
		switch (action.type) {
			case 'SELECT_BEAM': {
				const { index } = action;
				this.state.beamIndex = index;
				this.recalculate(index, this.state.beams[index]);
				break;
			}
			case 'DISTANCE_CHANGE':
				this.setBeamDistance(this.state.beamIndex, action.property.distance);
				break;
			case 'VALUE_CHANGE':
				this.setBeamProperty(this.state.beamIndex, action.property);
				break;
			case 'TOGGLE': {
				const isOn = this.state.beams[this.state.beamIndex].on;
				this.setBeamProperty(this.state.beamIndex, { on: !isOn });
				break;
			}
			default:
				break;
		}
		this.updateLocalStorage();
	}

	constructor() {
		super();
		try {
			const storedState = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
			if (storedState && storedState.storeVersion === initialState.storeVersion) {
				this.state = storedState;
			}
			else {
				this.state = initialState;
			}
		}
		catch (err) {
			this.state = initialState;
		}
		this.state.beams.forEach((beam, index) => {
			this.recalculate(index, beam);
		});
	}

	render() {
		const dispatch = this.dispatch.bind(this); // Do we need bind? Linter complains.
		return (
			<div id="app">
				<Tabs>
					<TabList>
						<Tab>BeamCalc</Tab>
						<Tab>Notes</Tab>
					</TabList>
					<TabPanel>
						{this.state.beamIndex > -1 ? <Numeric beam={this.state.beams[this.state.beamIndex]} dispatch={dispatch} /> : ''}
						<Graphic beams={this.state.beams} dispatch={dispatch} selectedBeamIndex={this.state.beamIndex} />
					</TabPanel>
					<TabPanel>
						<Notes />
					</TabPanel>
				</Tabs>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./components/app.js